import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Loader } from "../../../../../core/atoms/atoms";
import { apiPromiseHook } from "../../../../../shared/flux/actions/enterHooks";
import { MatchStateStatus, combineStateStatuses } from "../../../../../shared/helpers/reduxHelpers";
import { ActionPromises } from "../../../../state/actionPromises";
import { EnterHookPromises } from "../../../../state/enterHookPromises";
import { OldItinerary } from "./oldItinerary";
import { isFlagActive, FeatureFlag } from "../../../../../shared/helpers/featureFlags";
import { NewItinerary } from "./newItinerary";

const stateToProps = (state: IWebStoreState) => {
  const enquiry = state.customerEnquiry.data;

  // Latest message in the message thread
  // Used to determine if the customer is up to date
  let latestMessage: IMessage;

  if (enquiry.id && state.messagesByEnquiryId.data && state.messagesByEnquiryId.data[enquiry.id]) {
    const messageThread = state.messagesByEnquiryId.data[enquiry.id];
    latestMessage = (messageThread.thread.data || []).find(message => message.toCustomer);
  }

  return {
    user: state.user,
    ltoState: state.lto,
    enquiryState: state.customerEnquiry,
    allItinerariesState: state.customerItineraries,
    paymentsState: state.customerEnquiryPayments,
    latestMessage,
  };
};

const dispatchToProps = (dispatch) => {
  return {
    markItineraryViewed: (quoteId: number) => dispatch(ActionPromises.markItineraryViewed(quoteId)),
    markMessagesRead: (enquiryId: number) => dispatch(ActionPromises.markMessagesRead(enquiryId)),
  };
};

interface OwnProps {
  params: {
    /**
     * If not populated, redirect to current itinerary.
     */
    itineraryQuoteId: string | undefined;
  };
}

type PageProps = OwnProps & ReturnType<typeof dispatchToProps> & ReturnType<typeof stateToProps>;

const CustomerItineraryPage: React.FC<PageProps> = ({
  enquiryState,
  allItinerariesState,
  params,
  ltoState,
  paymentsState,
  latestMessage,
  markItineraryViewed,
  markMessagesRead,
  children,
}) => {

  useEffect(() => {
    const enquiry = enquiryState?.data;
    if (enquiry == null) {
      return;
    }

    if (!enquiry.messagingSummary.customerUpToDate) {
      markItineraryViewed(enquiry.currentQuoteId);
    }

    // Only mark messages as read if last message was an itinerary one
    if (latestMessage.messageType === MessageType.Itinerary) {
      markMessagesRead(enquiry.id);
    }
  }, []);

  return MatchStateStatus(combineStateStatuses(
    enquiryState.status,
    allItinerariesState.data?.[params.itineraryQuoteId]?.status,
    ltoState.status,
    paymentsState.status,
  ), {
    error: () => <p>Sorry, something went wrong. Please try reloading the page.</p>,
    pending: () => <Loader />,
    ok: () => {
      const newItineraryLayout = isFlagActive(FeatureFlag.CUSTOMER_ITINERARY_3008);

      const Component = newItineraryLayout
        ? NewItinerary
        : OldItinerary;

      const itinerary = allItinerariesState.data[params.itineraryQuoteId].data;

      return (
        <Component
          enquiry={enquiryState.data}
          itinerary={itinerary}
          payments={paymentsState.data}
          lto={ltoState.data}
        >
          {children}
        </Component>
      );
    }
  }) as JSX.Element;
}

export const Itinerary = connect(stateToProps, dispatchToProps)(CustomerItineraryPage);

export const itineraryOnEnter = apiPromiseHook(
  EnterHookPromises.enquiryItineraryFromRoute(),
  EnterHookPromises.paymentsByEnquiryId("enquiryId"),
  EnterHookPromises.customerEnquiryData("enquiryId"),
  EnterHookPromises.messagesByEnquiryId("enquiryId")
);
